<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :is-valid="isValid"
    :loading="loading"
    :title="`${$i18n.t('dialog.style.layerStylization')} ${isAdmin ? `- ${layer.name}` : ''}`"
    dialog-width="80%"
    body-height="65vh"
    @agree="acceptStyle"
    agree-text="dialog.agreeSave"
    disagree-text="dialog.disagree"
    max-height="85vh"
    no-paddings-y
    no-paddings-x
  >
    <template #body>
      <grey-tabs v-model="currentTab" :tabs="tabs" :show-arrows="false" :height="42" fill-height fill-width>
        <v-tab-item>
          <style-dialog-content v-bind="$props" @update:isValid="isValid = $event" />
        </v-tab-item>
        <v-tab-item v-if="isUserAdmin">
          <icons-settings class="pa-5" />
        </v-tab-item>
        <v-tab-item v-if="isAdmin">
          <v-row no-gutters>
            <v-col cols="10" class="px-5 py-6">
              <main-input-dialog-row v-for="input in fileInput" v-bind="input" :key="input.name">
                <template #file.input>
                  <v-row no-gutters v-if="hasFile">
                    <v-col cols="auto" class="text-left">
                      {{ qgisFileName }}
                    </v-col>
                    <v-col cols="auto" class="pl-10">
                      <base-button small @click="openDeleteDialog" translation-path="menu.delete"></base-button>
                    </v-col>
                  </v-row>
                  <v-row no-gutters v-else>
                    <v-col>
                      <file-input
                        @successfullyAdded="onSuccessfullyAdded"
                        ref="file"
                        :options="dropzoneOptions"
                      ></file-input>
                    </v-col>
                  </v-row>
                </template>
              </main-input-dialog-row>
            </v-col>
          </v-row>
        </v-tab-item>
      </grey-tabs>
      <main-dialog
        v-if="isDialogLoaded"
        :is-visible.sync="isDialogVisible"
        :title="$i18n.t('dialog.title.deleteQgisStyle')"
        :body="$i18n.t('dialog.body.deleteQgisStyle')"
        agree-text="dialog.agreeDelete"
        @agree="handleDeleteStyle"
        :loading="isConfirmButtonLoading"
      />
    </template>
  </main-large-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';

import FileInput from '@/components/FileInput';
import GreyTabs from '@/components/GreyTabs';
import IconsSettings from '@/components/IconsSettings';
import MainInputDialogRow from '@/components/MainInputDialogRow';
import StyleDialogContent from '@/components/StyleDialogContent';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'StyleDialog',
  components: {
    FileInput,
    GreyTabs,
    IconsSettings,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialogRow,
    MainLargeDialog,
    StyleDialogContent,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    layer: {
      type: Object,
      required: true,
    },
    attributes: {
      type: Array,
      required: true,
    },
    geomType: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    scope: {
      type: String,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    currentTab: 0,
    isConfirmButtonLoading: false,
    isDialogLoaded: false,
    isDialogVisible: false,
    isValid: false,
    qgisFileName: undefined,
  }),
  computed: {
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    dropzoneOptions() {
      return {
        url: `${process.env.VUE_APP_API_URL}/layers/features_layers/${this.layer.layer_id}/style_qgis`,
        method: 'put',
        autoProcessQueue: true,
        paramName: 'file',
        acceptedFiles: '.qml',
      };
    },
    fileInput() {
      return [
        {
          name: 'file',
          subtitle: 'dialog.body.uploadQmlFile',
          translationPath: 'dialog.body.qmlFile',
          rules: [v => !!v || 'rules.required'],
          isCustom: true,
        },
      ];
    },
    hasFile() {
      return this.$_hasAnyValue(this.qgisFileName);
    },
    isUserAdmin: get('users/currentUser@is_admin'),
    tabs() {
      const tabs = [
        {
          name: this.$i18n.t('dialog.body.style'),
        },
      ];
      if (this.isUserAdmin) {
        tabs.push({
          name: this.$i18n.t('dialog.body.symbols'),
        });
      }
      if (this.isAdmin) {
        tabs.push({
          name: this.$i18n.t('dialog.body.qgisStyle'),
        });
      }
      return tabs;
    },
  },
  methods: {
    deleteQgisStyle: call('layers/deleteQgisStyle'),
    getLayers: call('layers/getLayers'),
    async handleDeleteStyle() {
      try {
        this.isConfirmButtonLoading = true;
        await this.deleteQgisStyle({ layer_id: this.layer.layer_id, scope: this.scope });
        this.qgisFileName = undefined;
        this.isConfirmButtonLoading = false;
        this.isDialogVisible = false;
      } catch (error) {
        console.log(error);
        this.isConfirmButtonLoading = false;
      }
    },
    async onSuccessfullyAdded(e) {
      this.qgisFileName = e.name;
      await this.getLayers();
    },
    acceptStyle() {
      this.$emit('acceptStyle', this.layer);
    },
    openDeleteDialog() {
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
  },
  watch: {
    computedIsVisible: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.qgisFileName = this.layer.style_qgis_file_name;
        } else {
          this.qgisFileName = undefined;
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.title {
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.15);
  font-family: Roboto !important;
  font-style: normal !important;
  font-weight: normal !important;
  font-size: 18px !important;
  line-height: 16px !important;
  color: #202124 !important;
}
.style-elem-text {
  font-family: Roboto !important;
  font-style: normal !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  line-height: 15px !important;
  color: #202124 !important;
  text-align: left;
  padding-left: 9px !important;
}
.button-style {
  margin-left: 5px;
  color: white;
  border-radius: 2px;
  font-family: Roboto !important;
  font-style: normal !important;
  font-weight: 500 !important;
  font-size: 12px !important;
  line-height: 16px !important;
  text-transform: none !important;
}
.style-select {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 14px;
}
.border-top {
  border-top: 1px solid rgba(32, 33, 36, 0.18);
}
.border-bottom {
  border-bottom: 1px solid rgba(32, 33, 36, 0.18);
}
.border-left {
  border-left: 1px solid rgba(32, 33, 36, 0.18);
}
.style-class-element {
  min-height: 30px !important;
}
.style-class-title {
  text-align: left !important;
  font-family: Roboto !important;
  font-style: normal !important;
  font-weight: normal !important;
  font-size: 12px !important;
  line-height: 14px !important;
}
.style-class-active {
  font-weight: bold !important;
}
.single-class {
  border: 0.8px solid #898989;
  border-radius: 5px;
  text-align: left;
}
::v-deep {
  .v-list-item__title {
    @include selectListElement();
  }
  .v-slide-group {
    box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14);
  }
  .v-tab {
    border: none;
  }
  .v-tabs {
    z-index: 99;
    position: sticky;
    top: 0;
  }
  .v-tabs-items {
    height: calc(100% - 48px);
    overflow: auto;
  }
  .v-window__container,
  .v-window-item {
    height: 100%;
    padding-top: 0;
  }
  .v-data-table__wrapper {
    overflow-y: auto;
    overflow-x: auto;
    max-height: 100%;
  }
}
</style>
